import React from 'react';

export default function ValueObjectsExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Value Objects</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Value Object ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Objet immuable sans identité propre</li>
                                <li>Défini uniquement par ses attributs</li>
                                <li>Égalité basée sur les valeurs, pas sur l'identité</li>
                                <li>Auto-validant à la création</li>
                                <li>Encapsule des règles métier liées à ses valeurs</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure d'un Value Object</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`public record Amount(
    BigDecimal value,
    Currency currency
) {
    public Amount {
        // Validation à la construction
        if (value == null) {
            throw new IllegalArgumentException("Value cannot be null");
        }
        if (value.compareTo(BigDecimal.ZERO) < 0) {
            throw new IllegalArgumentException("Amount cannot be negative");
        }
        if (currency == null) {
            throw new IllegalArgumentException("Currency cannot be null");
        }
    }

    // Méthodes métier
    public Amount add(Amount other) {
        if (!this.currency.equals(other.currency)) {
            throw new IllegalArgumentException("Cannot add amounts with different currencies");
        }
        return new Amount(this.value.add(other.value), this.currency);
    }

    public Amount multiply(BigDecimal multiplier) {
        return new Amount(this.value.multiply(multiplier), this.currency);
    }

    public boolean isGreaterThan(Amount other) {
        checkSameCurrency(other);
        return this.value.compareTo(other.value) > 0;
    }

    private void checkSameCurrency(Amount other) {
        if (!this.currency.equals(other.currency)) {
            throw new IllegalArgumentException("Currency mismatch");
        }
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples Concrets</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">IBAN (International Bank Account Number)</h4>
                                <pre className="text-xs mt-2">
{`public record IBAN(String value) {
    public IBAN {
        // Validation du format
        if (!isValidIBANFormat(value)) {
            throw new IllegalArgumentException("Invalid IBAN format");
        }
    }

    public String getCountryCode() {
        return value.substring(0, 2);
    }

    public String getBankCode() {
        return value.substring(4, 12);
    }

    private static boolean isValidIBANFormat(String iban) {
        // Logique de validation IBAN
        return iban != null && 
               iban.matches("[A-Z]{2}\\d{2}[A-Z0-9]{4}\\d{7}([A-Z0-9]?){0,16}");
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">DatePeriod (Période de dates)</h4>
                                <pre className="text-xs mt-2">
{`public record DatePeriod(
    LocalDate startDate,
    LocalDate endDate
) {
    public DatePeriod {
        // Validation à la construction
        if (startDate == null || endDate == null) {
            throw new IllegalArgumentException("Dates cannot be null");
        }
        if (endDate.isBefore(startDate)) {
            throw new IllegalArgumentException("End date cannot be before start date");
        }
    }

    public boolean contains(LocalDate date) {
        return !date.isBefore(startDate) && !date.isAfter(endDate);
    }

    public long getDurationInDays() {
        return ChronoUnit.DAYS.between(startDate, endDate);
    }

    public boolean overlaps(DatePeriod other) {
        return !this.endDate.isBefore(other.startDate) && 
               !other.endDate.isBefore(this.startDate);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Utilisations Pratiques</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Dans une Entité</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`public class Payment {
    private final PaymentId id;
    private final Amount amount;
    private final IBAN debtorIban;
    private final IBAN creditorIban;
    private final DatePeriod validityPeriod;

    public void validateAmount(Amount maxLimit) {
        if (this.amount.isGreaterThan(maxLimit)) {
            throw new BusinessException("Amount exceeds limit");
        }
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Dans les Calculs</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`public class FeeCalculator {
    public Amount calculateFees(Amount transferAmount) {
        // Les opérations sur Amount sont sûres et auto-validées
        Amount baseFee = new Amount(
            BigDecimal.valueOf(10), 
            transferAmount.currency()
        );
        
        Amount percentageFee = transferAmount.multiply(
            BigDecimal.valueOf(0.001)
        );
        
        return baseFee.add(percentageFee);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Caractéristiques Essentielles</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Immutabilité</h4>
                            <p className="text-sm text-gray-600">
                                Une fois créé, un Value Object ne peut pas être modifié. Toute modification crée un nouvel objet.
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Égalité par Valeur</h4>
                            <p className="text-sm text-gray-600">
                                Deux Value Objects ayant les mêmes valeurs sont considérés comme égaux.
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Auto-Validation</h4>
                            <p className="text-sm text-gray-600">
                                Valide son état à la création et garantit son intégrité.
                            </p>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Comportement Riche</h4>
                            <p className="text-sm text-gray-600">
                                Encapsule la logique métier liée à ses valeurs.
                            </p>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Sans Effets de Bord</h4>
                            <p className="text-sm text-gray-600">
                                Les opérations retournent toujours de nouvelles instances.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}